import { Injectable, inject, OnDestroy, signal } from '@angular/core';
import { Subscription, interval, switchMap, catchError, EMPTY } from 'rxjs';
import { UserSessionService } from './user-session.service';
import { UserSessionDto } from '../models/api/userSessionDto';
import { environment } from '../../environments/environment';

const HEARTBEAT_INTERVAL_MS = 3 * 60 * 1000; // 3 minutes

@Injectable({
  providedIn: 'root'
})
export class UserSessionTrackerService implements OnDestroy {
  private sessionService = inject(UserSessionService);

  currentSession = signal<UserSessionDto | null>(null);
  private heartbeatSubscription: Subscription | null = null;
  private readonly onBeforeUnload = () => this.handleBeforeUnload();

  constructor() {
    window.addEventListener('beforeunload', this.onBeforeUnload);
  }

  ngOnDestroy(): void {
    this.endCurrentSession();
    window.removeEventListener('beforeunload', this.onBeforeUnload);
  }

  startTracking(companyId: string | null, section: string, year: string): void {
    if (!companyId) return;

    // End any existing session first
    this.endCurrentSession();

    this.sessionService.startSession({
      companyId,
      section,
      periodFrom: `${year}-01-01`,
      periodTo: `${year}-12-31`
    }).pipe(
      catchError(() => EMPTY)
    ).subscribe(session => {
      this.currentSession.set(session);
      this.startHeartbeat(session.id);
    });
  }

  endCurrentSession(): void {
    const session = this.currentSession();
    this.stopHeartbeat();
    this.currentSession.set(null);

    if (session) {
      this.sessionService.endSession(session.id).pipe(
        catchError(() => EMPTY)
      ).subscribe();
    }
  }

  recordCheckAction(companyId: string | null, section: string, year: string): void {
    if (!companyId) return;

    this.sessionService.checkAction({
      companyId,
      section,
      periodFrom: `${year}-01-01`,
      periodTo: `${year}-12-31`
    }).pipe(
      catchError(() => EMPTY)
    ).subscribe();
  }

  private startHeartbeat(sessionId: string): void {
    this.stopHeartbeat();

    this.heartbeatSubscription = interval(HEARTBEAT_INTERVAL_MS).pipe(
      switchMap(() => this.sessionService.heartbeat(sessionId).pipe(
        catchError(() => EMPTY)
      ))
    ).subscribe(response => {
      if (response && !response.active) {
        // Server ended the session (idle timeout)
        this.stopHeartbeat();
        this.currentSession.set(null);
      }
    });
  }

  private stopHeartbeat(): void {
    this.heartbeatSubscription?.unsubscribe();
    this.heartbeatSubscription = null;
  }

  private handleBeforeUnload(): void {
    const session = this.currentSession();
    if (!session) return;

    const url = `${environment.apiUrl}/user-sessions/${session.id}/end`;
    try {
      fetch(url, { method: 'PUT', keepalive: true });
    } catch {
      // Best effort — server will auto-end after 30 min
    }
  }
}
